// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { atom } from "jotai";

import { fetchJson } from "../../infra/fetchJson";
import { Project } from "./projects";

export * from "./folders";
export * from "./images";
export * from "./projects";
export * from "./resources";

export const LocalStorageKey = {
  // NOTE: `Language` and `ColorScheme` are also read pre-React by the boot
  // splash in index.html (which can't import this). Update that file if either
  // key is renamed — there is no TypeScript error to catch the drift.
  Language: "Backend_AI_FastTrack.preferences.language",
  TaskTemplateTray: "Backend_AI_FastTrack.collapse.taskTemplateTray",
  ColorScheme: "Backend_AI_FastTrack.preferences.colorScheme",
  AutoRefreshIntervals: "Backend_AI_FastTrack.preferences.autoRefreshIntervals",
};

export const coreServiceVersionAtom = atom(async (get) => {
  const response = await fetchJson<{
    version: string;
    manager: string;
  }>("/func/");
  return response;
});

export const serviceVersionAtom = atom(async (get) => {
  const response = await fetchJson<{
    name: string;
    version: string;
  }>("/health/");
  return response.version;
});

// The auth token is now stored in an HttpOnly cookie by the server.
// This atom tracks authentication state (not the token value itself).
// It is set to `true` after a successful login and `false` on logout.
export const isAuthenticatedAtom = atom<boolean>(false);

export type UserRole = "superadmin" | "admin" | "user";

export interface User {
  username: string;
  email: string;
  full_name: string;
  is_active: boolean;
  role: UserRole;
  domain_name: string;
  groups: Project[];
  resource_policy: string;
}

/**
 * Writable atom that holds the current user, synced from `useCurrentUser()`
 * (React Query) inside `AuthProvider`. Jotai atoms that need user data
 * (e.g. `projectListAtom`, `availableProjectListAtom`) read from this atom
 * instead of making their own network request.
 */
export const currentUserAtom = atom<User | null>(null);
